import type { PositionedText, TextLine } from "../types";

/**
 * Group positioned text items into visual lines.
 *
 * PDF text has no notion of "rows" — we reconstruct them by clustering items
 * that share (within a tolerance) the same baseline y on the same page, then
 * ordering each line left-to-right. This is the substrate every bank template
 * parses against.
 */
export function groupIntoLines(
  items: PositionedText[],
  yTolerance = 3,
): TextLine[] {
  const byPage = new Map<number, PositionedText[]>();
  for (const it of items) {
    const arr = byPage.get(it.page);
    if (arr) arr.push(it);
    else byPage.set(it.page, [it]);
  }

  const lines: TextLine[] = [];
  const pages = [...byPage.keys()].sort((a, b) => a - b);

  for (const page of pages) {
    const pageItems = byPage.get(page)!;
    // Top-to-bottom: PDF origin is bottom-left, so larger y is higher up.
    const sorted = [...pageItems].sort((a, b) => b.y - a.y);

    const buckets: PositionedText[][] = [];
    for (const it of sorted) {
      const bucket = buckets.find(
        (b) => Math.abs(b[0]!.y - it.y) <= yTolerance,
      );
      if (bucket) bucket.push(it);
      else buckets.push([it]);
    }

    for (const bucket of buckets) {
      bucket.sort((a, b) => a.x - b.x);
      lines.push({
        page,
        y: bucket[0]!.y,
        items: bucket,
        text: bucket
          .map((i) => i.text)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim(),
      });
    }
  }

  return lines;
}

/** Pick the text item at/after an x position, useful for column extraction. */
export function itemsInXRange(
  line: TextLine,
  xMin: number,
  xMax: number,
): PositionedText[] {
  // An item belongs to a column if its horizontal center falls in [xMin, xMax).
  return line.items.filter((it) => {
    const center = it.x + it.width / 2;
    return center >= xMin && center < xMax;
  });
}

/** Join the text of items in an x-range into a single trimmed string. */
export function textInXRange(line: TextLine, xMin: number, xMax: number): string {
  return itemsInXRange(line, xMin, xMax)
    .map((i) => i.text)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}
